/**
 * ChartManager Class - Manages the gold and silver price chart functionality
 * Handles data loading, chart initialization, and user interactions
 */
class ChartManager {
  /**
   * Constructor - Initializes the chart manager with default settings
   */
  constructor() {
    // Get CSV URL from data attribute
    this.csvUrl = document
      .querySelector(".chart-wrap")
      ?.getAttribute("data-csv-url");

    this.data = [];
    this.chart = null;
    this.currentMetal = "gold";

    // DOM element references
    this.container = document.getElementById("chart-container");
    this.chartCanvas = document.getElementById("chart");
    this.chartWrap = document.getElementById("chart-wrap");
    this.percentElement = document.getElementById("percent");
    this.btnGold = document.getElementById("btn-gold");
    this.btnSilver = document.getElementById("btn-silver");
    this.dataTableBody = document.querySelector("#chart-data-table tbody");

    // Bind methods to maintain context
    this.handleHover = this.handleHover.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);
    this.setMetal = this.setMetal.bind(this);
  }

  /**
   * Initialize the chart by loading data and setting up event listeners
   */
  async init() {
    try {
      this.setActiveAnnouncementBar();
      await this.loadData();
      this.buildDataTable();
      this.initializeChart();
      this.setupEventListeners();
      this.setMetal("gold");
    } catch (error) {
      this.container.classList.add("hidden");
      console.error("Error initializing chart:", error);
    }
  }

  /**
   * Load and parse CSV data from the specified URL
   */
  async loadData() {
    try {
      const response = await fetch(this.csvUrl);
      const csvText = await response.text();
      this.data = this.parseCsvToArray(csvText);
    } catch (error) {
      this.container.classList.add("hidden");
      console.error("Error loading CSV data:", error);
      throw error;
    }
  }

  /**
   * Set up scroll event listener to toggle active announcement bar based on A/B test variant
   * Implements throttling for performance and includes error handling for robustness
   */
  setActiveAnnouncementBar() {
    window.addEventListener('scroll', () => {

      const variant = document.body.dataset.abtestMessaging;
  
      
      const bar1 = document.querySelector('.shopify-section--announcement-bar[id$="__announcement-bar"]');
      const bar2 = document.querySelector('.shopify-section--announcement-bar[id$="__announcement-bar-abtest"]');
      
      if(!bar1 || !bar2) return 
      const activeBar = variant === "B" ? bar2 : bar1;
  
      activeBar.classList.add('active');
      (activeBar === bar1 ? bar2 : bar1).classList.remove('active');

      const height = activeBar.clientHeight;
      document.documentElement.style.setProperty('--announcement-bar-height', `${height}px`);
    })
  }

  /**
   * Parse CSV text into an array of data objects
   * @param {string} csv - The CSV text to parse
   * @returns {Array} Array of parsed data objects
   */
  parseCsvToArray(csv) {
    const lines = csv.trim().split(/\r?\n/);
    const dataLines = lines.slice(1);

    return dataLines
      .filter((line) => line.trim() !== "")
      .map((line) => {
        const cols = line.split(",").map((v) => v.replace(/[$\s]/g, "").trim());
        return {
          year: Number(cols[0]),
          month: cols[1],
          gold: parseFloat(cols[2]),
          silver: parseFloat(cols[3]),
        };
      });
  }

  /**
   * Build an accessible data table equivalent to the chart, for screen reader users
   * who cannot perceive the canvas rendering
   */
  buildDataTable() {
    if (!this.dataTableBody) return;

    const fragment = document.createDocumentFragment();

    this.data.forEach((item) => {
      const row = document.createElement("tr");

      const monthCell = document.createElement("th");
      monthCell.setAttribute("scope", "row");
      monthCell.textContent = `${item.month} ${item.year}`;

      const goldCell = document.createElement("td");
      goldCell.textContent = this.formatCurrency(item.gold);

      const silverCell = document.createElement("td");
      silverCell.textContent = this.formatCurrency(item.silver);

      row.append(monthCell, goldCell, silverCell);
      fragment.appendChild(row);
    });

    this.dataTableBody.replaceChildren(fragment);
  }

  /**
   * Get formatted labels for the chart
   * @returns {Array} Array of formatted label strings
   */
  getLabels() {
    return this.data.map((item) => `${item.year}-${item.month.slice(0, 3)}`);
  }

  /**
   * Get gold price data
   * @returns {Array} Array of gold prices
   */
  getGoldData() {
    return this.data.map((item) => item.gold);
  }

  /**
   * Get silver price data
   * @returns {Array} Array of silver prices
   */
  getSilverData() {
    return this.data.map((item) => item.silver);
  }

  /**
   * Calculate percentage change between two values
   * @param {number} first - Initial value
   * @param {number} last - Final value
   * @returns {number} Percentage change
   */
  calculatePercentageChange(first, last) {
    return (last / first - 1) * 100;
  }

  /**
   * Format percentage value with sign and decimal places
   * @param {number} value - Percentage value to format
   * @returns {string} Formatted percentage string
   */
  formatPercentage(value, symbol) {
    return (value >= 0 ? symbol : "") + value + "%";
  }

  /**
   * Format currency value with dollar sign and commas
   * @param {number} value - Currency value to format
   * @returns {string} Formatted currency string
   */
  formatCurrency(value) {
    let formatted = "$" + value.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, "$&,");
    return formatted.replace(/\.00$/, "");
  }

  /**
   * Create and configure the crosshair plugin for the chart
   * @returns {Object} Chart.js plugin configuration
   */
  createCrosshairPlugin() {
    return {
      id: "crosshair",
      afterDatasetsDraw: (chart) => {
        const {
          tooltip,
          chartArea: { top, bottom, left, right },
          ctx,
          scales,
        } = chart;
        const active = tooltip?.getActiveElements?.() || [];
        if (!active.length) return;

        // Get active point coordinates
        const dataPoint = tooltip.dataPoints[0];
        const x = tooltip.caretX;
        const y = scales.y.getPixelForValue(dataPoint.parsed.y);

        ctx.save();

        // Draw vertical dashed line
        ctx.beginPath();
        ctx.moveTo(x, top);
        ctx.lineTo(x, bottom);
        ctx.setLineDash([4, 4]);
        ctx.strokeStyle = "#00000080";
        ctx.lineWidth = 1;
        ctx.stroke();

        // Draw horizontal dashed line
        ctx.beginPath();
        ctx.moveTo(left, y);
        ctx.lineTo(right, y);
        ctx.setLineDash([4, 4]);
        ctx.strokeStyle = "#00000080";
        ctx.lineWidth = 1;
        ctx.stroke();

        // Draw optional ring around the point
        ctx.setLineDash([4, 4]);
        ctx.lineWidth = 1;
        ctx.strokeStyle = "#00000080";
        ctx.beginPath();
        ctx.arc(x, y, 10, 0, Math.PI * 2);
        ctx.stroke();

        // Draw central point
        ctx.setLineDash([]);
        ctx.fillStyle = "#ffffff";
        ctx.beginPath();
        ctx.arc(x, y, 3.5, 0, Math.PI * 2);
        ctx.fill();

        ctx.restore();
      },
    };
  }

  /**
   * Initialize the Chart.js instance
   */
  initializeChart() {
    const ctx = this.chartCanvas.getContext("2d");
    const labels = this.getLabels();
    const goldData = this.getGoldData();

    this.chart = new Chart(ctx, {
      type: "line",
      data: {
        labels,
        datasets: [
          {
            label: "Gold",
            data: goldData,
            borderColor: "#d3b28e",
            borderWidth: 3,
            tension: 0,
            pointRadius: 0,
            pointHoverRadius: 4,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: { display: false },
          tooltip: {
            enabled: false,
            external: () => {},
          },
        },
        scales: {
          x: {
            ticks: {
              callback: (value, index) =>
                /-Jan$/.test(labels[index]) ? labels[index].slice(0, 4) : "",
              maxRotation: 0,
              minRotation: 0,
              font: function (context) {
                let width = context.chart.width;
                let size = Math.round(
                  width / (window.innerWidth <= 768 ? 30 : 64)
                );

                return {
                  size: size,
                };
              },
              color: "#000000",
            },
            grid: { display: false },
            border: { display: false },
          },
          y: {
            ticks: { display: false },
            grid: { display: false },
            border: { display: false },
          },
        },
        onHover: this.handleHover,
      },
      plugins: [this.createCrosshairPlugin()],
    });
  }

  /**
   * Handle mouse hover events on the chart
   * @param {Event} event - Mouse event
   * @param {Array} activeElements - Active chart elements
   * @param {Object} chart - Chart instance
   */
  handleHover(event, activeElements, chart) {
    const points = chart.getElementsAtEventForMode(
      event,
      "index",
      { intersect: false },
      false
    );

    if (!points.length) {
      this.hideTooltips();
      this.updateGlobalPercentage();
      return;
    }

    const index = points[0].index;
    const dataset = chart.data.datasets[0];
    const value = dataset.data[index];
    const initialValue = dataset.data[0];
    const percentageChange = this.calculatePercentageChange(
      initialValue,
      value
    );
    const [year, month] = chart.data.labels[index].split("-");

    // Get point coordinates
    const metaPoint = chart.getDatasetMeta(0).data[index];
    const x = metaPoint.x;
    const y = metaPoint.y;

    // Calculate offsets
    const wrapRect = this.chartWrap.getBoundingClientRect();
    const canvasRect = chart.canvas.getBoundingClientRect();
    const offsetLeft = canvasRect.left - wrapRect.left;
    const offsetTop = canvasRect.top - wrapRect.top;
    const chartArea = chart.chartArea;

    // Show tooltips
    this.showTooltip(
      "tip-month",
      month,
      offsetLeft + x,
      offsetTop + chartArea.bottom + 10
    );
    this.showTooltip(
      "tip-pct",
      this.formatCurrency(value),
      offsetLeft + chartArea.left - 10,
      offsetTop + y + 20
    );

    // Update percentage display
    this.percentElement.textContent = this.formatCurrency(value) + "/oz";
  }

  /**
   * Handle mouse leave events on the chart canvas
   */
  handleMouseLeave() {
    this.hideTooltips();
    this.updateGlobalPercentage();
  }

  /**
   * Show or create a tooltip element
   * @param {string} id - Tooltip element ID
   * @param {string} text - Tooltip text content
   * @param {number} left - Left position
   * @param {number} top - Top position
   */
  showTooltip(id, text, left, top) {
    let tooltip = document.getElementById(id);
    if (!tooltip) {
      tooltip = document.createElement("div");
      tooltip.id = id;
      tooltip.className = "chartjs-tooltip";
      this.chartWrap.appendChild(tooltip);
    }
    tooltip.textContent = text;
    tooltip.style.left = left + "px";
    tooltip.style.top = top + "px";
    tooltip.style.display = "block";
  }

  /**
   * Hide all tooltip elements
   */
  hideTooltips() {
    const tooltips = ["tip-pct", "tip-month"];
    tooltips.forEach((id) => {
      const tooltip = document.getElementById(id);
      if (tooltip) {
        tooltip.style.display = "none";
      }
    });
  }

  /**
   * Update the global percentage display
   */
  updateGlobalPercentage() {
    const dataset = this.chart.data.datasets[0];
    const initialValue = dataset.data[0];
    const finalValue = dataset.data[dataset.data.length - 1];
    const percentageChange = this.calculatePercentageChange(
      initialValue,
      finalValue
    );
    this.percentElement.textContent = this.formatCurrency(finalValue) + "/oz";
  }

  /**
   * Set the active metal (gold or silver) and update the chart
   * @param {string} metal - 'gold' or 'silver'
   */
  setMetal(metal) {
    this.currentMetal = metal;
    const dataset = this.chart.data.datasets[0];

    if (metal === "gold") {
      this.btnGold.setAttribute("aria-pressed", "true");
      this.btnSilver.setAttribute("aria-pressed", "false");
      dataset.label = "Gold";
      dataset.borderColor = "#DDBFA0";
      dataset.data = this.getGoldData();
    } else {
      this.btnSilver.setAttribute("aria-pressed", "true");
      this.btnGold.setAttribute("aria-pressed", "false");
      dataset.label = "Silver";
      dataset.borderColor = "#BABABA";
      dataset.data = this.getSilverData();
    }

    this.chart.update();
    this.updateGlobalPercentage();
  }

  /**
   * Set up event listeners for user interactions
   */
  setupEventListeners() {
    this.chart.canvas.addEventListener("mouseleave", this.handleMouseLeave);
    this.btnGold.addEventListener("click", () => this.setMetal("gold"));
    this.btnSilver.addEventListener("click", () => this.setMetal("silver"));
  }
}

// Initialize the chart when the DOM is ready
document.addEventListener("DOMContentLoaded", () => {
  const chartManager = new ChartManager();
  chartManager.init();
});
